import React, { useEffect, useMemo, useState } from 'react'
import { useExpense } from '../contexts/ExpenseContext'
import { Plus, Search, Download, Edit, Trash2, Repeat } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import ExportModal from '../components/ExportModal'
import { formatRupees } from '../utils/currency'
import { accountService } from '../lib/api'

const ExpensesPage = () => {
  const { 
    expenses, 
    budgets,
    loading, 
    addExpense, 
    updateExpense, 
    deleteExpense, 
    exportExpenses 
  } = useExpense()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const [accounts, setAccounts] = useState([])

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    category: 'Other',
    budget_id: '',
    account_id: '',
    description: '',
    is_recurring: false,
    recurring_frequency: ''
  })

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const accountData = await accountService.getAccounts()
        setAccounts(accountData.accounts || [])
      } catch (error) {
        console.error('Failed to load accounts for expense form:', error)
      }
    }

    loadAccounts()
  }, [])

  const categories = [
    'Food',
    'Rent',
    'Groceries',
    'Transportation',
    'Entertainment',
    'Healthcare',
    'Shopping',
    'Bills',
    'Utilities',
    'Education',
    'Other'
  ]

  const budgetsById = useMemo(() => {
    return budgets.reduce((acc, budget) => {
      acc[budget.id] = budget
      return acc
    }, {})
  }, [budgets])

  const monthlyBudgets = budgets.filter(budget => (budget.source_type || 'monthly') !== 'extra')
  const extraBudgets = budgets.filter(budget => (budget.source_type || 'monthly') === 'extra')

  const handleAddExpense = async (e) => {
    e.preventDefault()

    const selectedBudget = formData.budget_id ? budgetsById[formData.budget_id] : null

    const payload = {
      ...formData,
      amount: parseFloat(formData.amount),
      category: selectedBudget?.category || formData.category,
      recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null
    }

    if (formData.budget_id) {
      payload.budget_id = formData.budget_id
    }

    try {
      await addExpense(payload)
      setFormData({
        title: '',
        amount: '',
        category: 'Other',
        budget_id: '',
        account_id: '',
        description: '',
        is_recurring: false,
        recurring_frequency: ''
      })
      setShowAddModal(false)
    } catch (error) {
      console.error('Error adding expense:', error)
    }
  }

  const handleExport = async (format, filename) => {
    try {
      setExportLoading(true)
      await exportExpenses(format, filename)
      setShowExportModal(false)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setExportLoading(false)
    }
  }

  const handleEditExpense = async (e) => {
    e.preventDefault()

    const selectedBudget = formData.budget_id ? budgetsById[formData.budget_id] : null

    const payload = {
      title: formData.title,
      amount: parseFloat(formData.amount),
      category: selectedBudget?.category || formData.category,
      description: formData.description,
      is_recurring: formData.is_recurring,
      recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null,
      account_id: formData.account_id || null,
    }

    if (formData.budget_id || editingExpense?.budget_id) {
      payload.budget_id = formData.budget_id || null
    }

    try {
      await updateExpense(editingExpense.id, payload)
      
      setShowEditModal(false)
      setEditingExpense(null)
      setFormData({
        title: '',
        amount: '',
        category: 'Other',
        budget_id: '',
        account_id: '',
        description: '',
        is_recurring: false,
        recurring_frequency: ''
      })
    } catch (error) {
      console.error('Error updating expense:', error)
    }
  }

  const handleDeleteExpense = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteExpense(expenseId)
      } catch (error) {
        console.error('Error deleting expense:', error)
      }
    }
  }

  const startEditExpense = (expense) => {
    setEditingExpense(expense)
    setFormData({
      title: expense.title,
      amount: expense.amount.toString(),
      category: expense.category,
      budget_id: expense.budget_id || '',
      account_id: expense.account_id || '',
      description: expense.description || '',
      is_recurring: expense.is_recurring || false,
      recurring_frequency: expense.recurring_frequency || ''
    })
    setShowEditModal(true)
  }

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expense.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = !categoryFilter || expense.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  if (loading) {
    return <LoadingSpinner message="Loading expenses..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Expenses
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your expenses and track spending
          </p>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          
          <div className="sm:w-48">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-field"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Simple expenses list */}
      <div className="card">
        {filteredExpenses.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No expenses found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Start by adding your first expense
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              Add Expense
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredExpenses.map((expense) => (
              <div key={expense.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {expense.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {expense.category} • {new Date(expense.created_at).toLocaleDateString()}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    {expense.is_recurring && (
                      <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 inline-flex items-center gap-1">
                        <Repeat className="h-3 w-3" />
                        Recurs {expense.recurring_frequency || 'monthly'}
                      </span>
                    )}

                    {(expense.budget_source_type || expense.budget_id) && (
                      <span className={`px-2 py-1 rounded-full ${
                        (expense.budget_source_type || 'monthly') === 'extra'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                      }`}>
                        {(expense.budget_source_type || 'monthly') === 'extra'
                          ? `Extra • ${expense.budget_source_name || 'source'}`
                          : 'Monthly Budget'}
                      </span>
                    )}

                    {expense.account_id && (
                      <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        Account linked
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatRupees(expense.amount)}
                    </p>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => startEditExpense(expense)}
                      className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      title="Edit expense"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteExpense(expense.id)}
                      className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Delete expense"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Basic Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Add New Expense
            </h2>
            
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  placeholder="Enter expense title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="input-field"
                  placeholder="Enter amount in ₹"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="input-field"
                  disabled={Boolean(formData.budget_id)}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {formData.budget_id && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Category is locked to the selected budget allocation.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Spend From (Optional)
                </label>
                <select
                  value={formData.budget_id}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    const selectedBudget = budgetsById[selectedId]
                    setFormData(prev => ({
                      ...prev,
                      budget_id: selectedId,
                      category: selectedBudget?.category || prev.category
                    }))
                  }}
                  className="input-field"
                >
                  <option value="">Not linked to a budget</option>

                  {monthlyBudgets.length > 0 && (
                    <optgroup label="Monthly Budget Allocations">
                      {monthlyBudgets.map(budget => (
                        <option key={budget.id} value={budget.id}>
                          {budget.category} • {formatRupees(budget.amount)} ({budget.period})
                        </option>
                      ))}
                    </optgroup>
                  )}

                  {extraBudgets.length > 0 && (
                    <optgroup label="Extra Budget Allocations">
                      {extraBudgets.map(budget => (
                        <option key={budget.id} value={budget.id}>
                          {budget.category} • {formatRupees(budget.amount)} ({budget.source_name || 'Extra'})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account (Optional)
                </label>
                <select
                  value={formData.account_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, account_id: e.target.value }))}
                  className="input-field"
                >
                  <option value="">No account link</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      is_recurring: e.target.checked,
                      recurring_frequency: e.target.checked ? (prev.recurring_frequency || 'monthly') : ''
                    }))}
                    className="rounded"
                  />
                  This is a fixed recurring expense
                </label>
              </div>

              {formData.is_recurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recurring Frequency
                  </label>
                  <select
                    value={formData.recurring_frequency || 'monthly'}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurring_frequency: e.target.value }))}
                    className="input-field"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  Add Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Edit Expense
            </h2>
            
            <form onSubmit={handleEditExpense} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  placeholder="Enter expense title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="input-field"
                  placeholder="Enter amount in ₹"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="input-field"
                  disabled={Boolean(formData.budget_id)}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {formData.budget_id && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Category is locked to the selected budget allocation.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Spend From (Optional)
                </label>
                <select
                  value={formData.budget_id}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    const selectedBudget = budgetsById[selectedId]
                    setFormData(prev => ({
                      ...prev,
                      budget_id: selectedId,
                      category: selectedBudget?.category || prev.category
                    }))
                  }}
                  className="input-field"
                >
                  <option value="">Not linked to a budget</option>

                  {monthlyBudgets.length > 0 && (
                    <optgroup label="Monthly Budget Allocations">
                      {monthlyBudgets.map(budget => (
                        <option key={budget.id} value={budget.id}>
                          {budget.category} • {formatRupees(budget.amount)} ({budget.period})
                        </option>
                      ))}
                    </optgroup>
                  )}

                  {extraBudgets.length > 0 && (
                    <optgroup label="Extra Budget Allocations">
                      {extraBudgets.map(budget => (
                        <option key={budget.id} value={budget.id}>
                          {budget.category} • {formatRupees(budget.amount)} ({budget.source_name || 'Extra'})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account (Optional)
                </label>
                <select
                  value={formData.account_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, account_id: e.target.value }))}
                  className="input-field"
                >
                  <option value="">No account link</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field"
                  placeholder="Enter expense description"
                  rows="3"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      is_recurring: e.target.checked,
                      recurring_frequency: e.target.checked ? (prev.recurring_frequency || 'monthly') : ''
                    }))}
                    className="rounded"
                  />
                  This is a fixed recurring expense
                </label>
              </div>

              {formData.is_recurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recurring Frequency
                  </label>
                  <select
                    value={formData.recurring_frequency || 'monthly'}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurring_frequency: e.target.value }))}
                    className="input-field"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingExpense(null)
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  Update Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isLoading={exportLoading}
        title="Export Expenses"
      />
    </div>
  )
}

export default ExpensesPage
